import React, { useEffect, useState, useRef } from 'react';
import { CSVLink } from 'react-csv';
import DataTable from 'react-data-table-component';
import { DatePicker, Tooltip, Space, Tag, Button, Input } from 'antd';
import { FileExcelOutlined, DownloadOutlined, EyeOutlined } from '@ant-design/icons';
import PropagateLoader from "react-spinners/PropagateLoader";
import { getUTCToLocalDateTimeFormat, toDayDate } from '../../../Utils/function';
import { getRequest, downloadRequest } from '../../../Utils/AppApi';
import { useHistory } from "react-router-dom";
import { toast } from 'react-toastify';
import { GET_BATCH_TRANSACTIONS_REPORT } from '../../../Utils/appConstants';

const { RangePicker } = DatePicker;

// ---- Error Boundary for expanded rows ----
class ExpandedRowErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = { hasError: false, error: null };
    }
    static getDerivedStateFromError(error) {
        return { hasError: true, error };
    }
    componentDidCatch(error, errorInfo) {
        console.error('❌ Expanded row error:', error);
        console.error('Stack trace:', errorInfo.componentStack);
    }
    render() {
        if (this.state.hasError) {
            return (
                <div className="text-center p-3 text-danger">
                    <p>Something went wrong while loading batch details.</p>
                    <details style={{ textAlign: 'left', background: '#f5f5f5', padding: '10px', borderRadius: '4px' }}>
                        <summary>Error details (click to expand)</summary>
                        <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                            {this.state.error?.toString()}
                        </pre>
                    </details>
                </div>
            );
        }
        return this.props.children;
    }
}

function BatchReport() {
    const history = useHistory();
    const csvLink = useRef();
    const limit = 10;
    const [isLoading, setLoading] = useState(false);
    const [dateRange, setDateRange] = useState([]); // Start with empty (no filter)
    const [searchTransId, setSearchTransId] = useState('');
    const [batches, setBatches] = useState([]);
    const [totalRecords, setTotalRecords] = useState(0);
    const [page, setPage] = useState(1);
    const [perPage, setPerPage] = useState(limit);
    const [csvData, setCsvData] = useState([]);
    const [expandedRowIds, setExpandedRowIds] = useState(new Set());
    const [batchDetails, setBatchDetails] = useState({});
    const [rawResponse, setRawResponse] = useState(null); // for debugging

    // ---- Helpers ----
    const formatDateForAPI = (date) => {
        if (!date) return '';
        const d = new Date(date);
        const year = d.getFullYear();
        const month = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    const objectToQueryString = (obj) => {
        const str = [];
        for (const p in obj) {
            if (obj.hasOwnProperty(p) && obj[p] !== undefined && obj[p] !== '') {
                str.push(encodeURIComponent(p) + "=" + encodeURIComponent(obj[p]));
            }
        }
        return str.join("&");
    };

    // ---- Fetch batches (list or detail) ----
    const fetchBatches = async (params = {}) => {
        const queryString = objectToQueryString(params);
        setLoading(true);
        try {
            const response = await getRequest(`${GET_BATCH_TRANSACTIONS_REPORT}?${queryString}`, history);
            console.log('📦 Full API response:', response);
            setRawResponse(JSON.stringify(response, null, 2)); // store for display

            if (response.success) {
                // ---- Single batch detail ----
                if (params.batchId) {
                    return response.data; // full batch
                }

                // ---- Parse list response ----
                const data = response.data || {};
                let docs = data.docs || data.rows || data.items || data.records || [];
                if (!Array.isArray(docs)) {
                    // If data itself is an array, use it
                    if (Array.isArray(data)) {
                        docs = data;
                    }
                    // If data is an object with a batchId, wrap it (single item)
                    else if (data.batchId) {
                        docs = [data];
                    }
                }

                const total = data.totalDocs || data.total || data.totalRows || data.totalItems || docs.length || 0;

                setBatches(docs);
                setTotalRecords(total);
                setPage(data.page || data.currentPage || 1);
                console.log(`✅ Loaded ${docs.length} batches, total ${total}`);
            } else {
                setBatches([]);
                setTotalRecords(0);
                toast.error(response.message || 'Failed to fetch batches');
            }
        } catch (err) {
            console.error('❌ Fetch error:', err);
            toast.error('Error fetching batches');
        } finally {
            setLoading(false);
        }
        return null;
    };

    // ---- Fetch detail for expanded row ----
    const fetchBatchDetail = async (batchId) => {
        if (batchDetails[batchId]) return;
        try {
            const detail = await fetchBatches({ batchId });
            if (detail) {
                setBatchDetails(prev => ({ ...prev, [batchId]: detail }));
            }
        } catch (err) {
            console.error('Error fetching batch detail:', err);
            toast.error('Failed to load batch details');
        }
    };

    // ---- Expand handler ----
    const handleExpand = (row, isExpanded) => {
        const newSet = new Set(expandedRowIds);
        if (isExpanded) {
            newSet.add(row.batchId);
            if (!batchDetails[row.batchId]) {
                fetchBatchDetail(row.batchId);
            }
        } else {
            newSet.delete(row.batchId);
        }
        setExpandedRowIds(newSet);
    };

    // ---- Export CSV ----
    const getCSVDataList = () => {
        const rows = batches.map(batch => ({
            'Batch ID': batch.batchId,
            'File Name': batch.fileName || '-',
            'Uploaded By': batch.uploadedBy?.username || batch.uploadedBy?.name || '-',
            'Total Records': batch.totalRecords,
            'Success': batch.successRecords,
            'Failed': batch.failedRecords,
            'Status': batch.status,
            'Uploaded At': getUTCToLocalDateTimeFormat(batch.uploadedAt),
        }));
        setCsvData(rows);
        setTimeout(() => {
            csvLink.current.link.click();
        }, 500);
    };

    // ---- Export Excel ----
    const exportBatchResults = async (batchId) => {
        try {
            const exportUrl = `/fund-transfer/upload-batch/${batchId}/export`;
            const response = await downloadRequest(exportUrl, history);
            const blob = response.data;
            const contentDisposition = response.headers['content-disposition'];
            let filename = `upload_batch_${batchId}.xlsx`;
            if (contentDisposition) {
                const match = contentDisposition.match(/filename="?([^"]+)"?/);
                if (match) filename = match[1];
            }
            const link = document.createElement('a');
            link.href = URL.createObjectURL(blob);
            link.download = filename;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(link.href);
            toast.success('📊 Batch results downloaded');
        } catch (error) {
            console.error('Export error:', error);
            toast.error(error?.message || 'Failed to export batch results');
        }
    };

    // ---- Search / Clear ----
    const handleSearch = () => {
        setPage(1);
        fetchBatches({
            page: 1,
            limit: perPage,
            startDate: dateRange?.[0] ? formatDateForAPI(dateRange[0]) : '',
            endDate: dateRange?.[1] ? formatDateForAPI(dateRange[1]) : '',
            searchTransId: searchTransId.trim() || '',
        });
    };

    const handleClear = () => {
        setDateRange([]);
        setSearchTransId('');
        setPage(1);
        fetchBatches({
            page: 1,
            limit: perPage,
        });
    };

    // ---- Pagination ----
    const handlePageChange = (pageNum) => {
        setPage(pageNum);
        fetchBatches({
            page: pageNum,
            limit: perPage,
            startDate: dateRange?.[0] ? formatDateForAPI(dateRange[0]) : '',
            endDate: dateRange?.[1] ? formatDateForAPI(dateRange[1]) : '',
            searchTransId: searchTransId.trim() || '',
        });
    };

    const handlePerRowsChange = (newPerPage) => {
        setPerPage(newPerPage);
        setPage(1);
        fetchBatches({
            page: 1,
            limit: newPerPage,
            startDate: dateRange?.[0] ? formatDateForAPI(dateRange[0]) : '',
            endDate: dateRange?.[1] ? formatDateForAPI(dateRange[1]) : '',
            searchTransId: searchTransId.trim() || '',
        });
    };

    // ---- Initial load ----
    useEffect(() => {
        fetchBatches({
            page: 1,
            limit: perPage,
        });
        // eslint-disable-next-line
    }, []);

    // ---- Columns ----
    const columns = [
        {
            name: 'Batch ID',
            selector: row => row.batchId,
            width: '200px',
            cell: row => <Tooltip title={row.batchId}><span className="text-primary font-weight-bold">{row.batchId}</span></Tooltip>
        },
        {
            name: 'File Name',
            selector: row => row.fileName || '-',
            width: '180px',
            cell: row => <Tooltip title={row.fileName}><span>{row.fileName || '-'}</span></Tooltip>
        },
        {
            name: 'Uploaded By',
            selector: row => row.uploadedBy?.username || row.uploadedBy?.name || '-',
            width: '150px',
        },
        {
            name: 'Total',
            selector: row => row.totalRecords,
            width: '70px',
            center: true,
        },
        {
            name: 'Success',
            selector: row => row.successRecords,
            width: '70px',
            center: true,
            cell: row => <Tag color="green">{row.successRecords}</Tag>
        },
        {
            name: 'Failed',
            selector: row => row.failedRecords,
            width: '70px',
            center: true,
            cell: row => <Tag color="red">{row.failedRecords}</Tag>
        },
        // {
        //     name: 'Status',
        //     selector: row => row.status,
        //     width: '100px',
        //     cell: row => {
        //         const color = row.status === 'success' ? 'green' : row.status === 'failed' ? 'red' : 'orange';
        //         return <Tag color={color}>{row.status}</Tag>;
        //     }
        // },
        {
            name: 'Uploaded At',
            selector: row => getUTCToLocalDateTimeFormat(row.uploadedAt),
            width: '180px',
        },
        {
            name: 'Actions',
            width: '160px',
            cell: row => (
                <Space>
                    <Tooltip title="Download results">
                        <Button icon={<DownloadOutlined />} size="small" onClick={() => exportBatchResults(row.batchId)} />
                    </Tooltip>
                    {/* <Tooltip title="View details (expand row)">
                        <Button icon={<EyeOutlined />} size="small" onClick={() => {
                            const isExpanded = expandedRowIds.has(row.batchId);
                            handleExpand(row, !isExpanded);
                        }} />
                    </Tooltip> */}
                </Space>
            )
        }
    ];

    // ---- Expanded row ----
    const ExpandedRowComponent = ({ data }) => {
        console.log('🔍 ExpandedRow data:', data);

        if (!data || typeof data !== 'object' || !data.batchId) {
            return <div className="text-center p-3 text-danger">Invalid batch data (row object missing)</div>;
        }

        const batch = data;
        const details = batchDetails[batch.batchId];
        if (!details) {
            return <div className="text-center p-3"><PropagateLoader color="#5851D2" size={15} /></div>;
        }

        const transactions = Array.isArray(details.dataRows) ? details.dataRows : [];
        const results = Array.isArray(details.results) ? details.results : [];
        const header = Array.isArray(details.headerRow) ? details.headerRow : [];

        if (transactions.length === 0) {
            return <div className="text-center p-3">No transactions in this batch</div>;
        }

        try {
            const txnIdIndex = header.indexOf('Customer Reference Number');
            const rowsWithResults = transactions.map((row, idx) => {
                if (!Array.isArray(row)) {
                    return {
                        rowNumber: idx + 1,
                        rowData: [],
                        transactionId: '',
                        resultStatus: '-',
                        message: '-',
                        bankRefNum: '-',
                    };
                }
                const txnId = (txnIdIndex !== -1 && row[txnIdIndex]) ? String(row[txnIdIndex]).trim() : '';
                const result = results.find(r => r.transactionId === txnId) || {};
                return {
                    rowNumber: idx + 1,
                    rowData: row,
                    transactionId: txnId,
                    resultStatus: result.result || '-',
                    message: result.message || '-',
                    bankRefNum: result.bankRefNum || '-',
                };
            });

            const subColumns = [
                { name: '#', selector: 'rowNumber', width: '60px' },
                ...header.map((col, idx) => ({
                    name: col,
                    selector: row => row.rowData[idx] || '-',
                    width: 'auto',
                })),
                { name: 'Result', selector: 'resultStatus', width: '100px', cell: row => <Tag color={row.resultStatus === 'success' ? 'green' : 'red'}>{row.resultStatus}</Tag> },
                { name: 'Message', selector: 'message', width: '200px' },
            ];

            return (
                <div style={{ padding: '10px 20px', background: '#f9f9f9' }}>
                    <div className="row mb-2">
                        <div className="col-md-12">
                            <h5>Transactions in Batch {batch.batchId}</h5>
                            <span>Total: {transactions.length}</span>
                        </div>
                    </div>
                    <DataTable
                        columns={subColumns}
                        data={rowsWithResults}
                        highlightOnHover
                        pagination
                        paginationPerPage={10}
                        paginationRowsPerPageOptions={[5, 10, 25]}
                        noHeader
                    />
                </div>
            );
        } catch (err) {
            console.error('💥 Expanded row render error:', err);
            return <div className="text-center p-3 text-danger">Error rendering transactions: {err.message}</div>;
        }
    };

    const SafeExpandedRow = (props) => (
        <ExpandedRowErrorBoundary>
            <ExpandedRowComponent {...props} />
        </ExpandedRowErrorBoundary>
    );

    // ---- Loading ----
    if (isLoading && batches.length === 0) {
        return <div className="content text-center" style={{ margin: "250px 0" }}>
            <PropagateLoader color="#5851D2" loading={isLoading} size={25} />
        </div>;
    }

    // ---- Main render ----
    return (
        <div>
            <div className="content-wrapper pt-2">
                <div className="content-header my-0 py-0">
                    <div className="container-fluid">
                        <div className="row mb-0 align-items-center">
                            <div className="col-sm-6">
                                <h1 className='page-title text-blue font-merriweather'>Batch <span className='text-red'>Report</span></h1>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="content">
                    <div className="container-fluid pt-2 pb-5">
                        <div className="row">
                            <div className='card col-md-12 py-3'>
                                <div className='card-body p-0'>
                                    {/* Filters */}
                                    <div className="row">
                                        <div className='col-md-12 date-search mb-0'>
                                            <div className='row'>
                                                <div className='col-md-4 d-flex align-items-end'>
                                                    <div className="form-group mr-3" style={{ flex: 1 }}>
                                                        <label className='date-search-label mb-0 text-blue'>Date Range</label>
                                                        <RangePicker
                                                            className="form-control form-control-border"
                                                            value={dateRange}
                                                            format="DD-MM-YYYY"
                                                            onChange={(dates) => setDateRange(dates)}
                                                            style={{ width: '100%' }}
                                                        />
                                                    </div>
                                                </div>
                                                <div className='col-md-4 d-flex align-items-end'>
                                                    <div className="form-group mr-3" style={{ flex: 1 }}>
                                                        <label className='date-search-label mb-0 text-red'>Transaction ID</label>
                                                        <Input
                                                            className="form-control form-control-border"
                                                            placeholder="Search by Transaction ID"
                                                            value={searchTransId}
                                                            onChange={(e) => setSearchTransId(e.target.value)}
                                                        />
                                                    </div>
                                                </div>
                                                <div className='col-md-4 d-flex align-items-end justify-content-end'>
                                                    <Button type="primary" className="mr-2" onClick={handleSearch}>Search</Button>
                                                    <Button className="mr-2" onClick={handleClear}>Clear</Button>
                                                    <Button type="primary" className="mr-2" onClick={getCSVDataList}>
                                                        <FileExcelOutlined /> Export CSV
                                                    </Button>
                                                    <CSVLink
                                                        data={csvData}
                                                        filename='batch_report'
                                                        className='hidden'
                                                        ref={csvLink}
                                                        target='_blank'
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Debug: Raw Response (only in dev) */}
                                    {/* {rawResponse && (
                                        <details style={{ marginBottom: '15px', background: '#f0f0f0', padding: '10px', borderRadius: '4px' }}>
                                            <summary style={{ cursor: 'pointer', fontWeight: 'bold' }}>📄 Raw API Response (click to expand)</summary>
                                            <pre style={{ maxHeight: '300px', overflow: 'auto', background: '#fff', padding: '10px', fontSize: '12px' }}>
                                                {rawResponse}
                                            </pre>
                                        </details>
                                    )} */}

                                    {/* DataTable */}
                                    <div className="container-fluid pt-2 pb-5">
                                        {batches.length === 0 && !isLoading ? (
                                            <div className="text-center p-5">
                                                <h4>No batches found</h4>
                                                <p>Try adjusting your filters or upload a new file.</p>
                                            </div>
                                        ) : (
                                            <DataTable
                                                title={""}
                                                columns={columns}
                                                data={batches}
                                                highlightOnHover
                                                pagination
                                                paginationServer
                                                paginationTotalRows={totalRecords}
                                                onChangeRowsPerPage={handlePerRowsChange}
                                                onChangePage={handlePageChange}
                                                // expandableRows
                                                // expandableRowsComponent={SafeExpandedRow}
                                                // onRowExpandToggled={(expanded, row) => handleExpand(row, expanded)}
                                                // expandableRowExpanded={row => expandedRowIds.has(row.batchId)}
                                                progressPending={isLoading}
                                            />
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default BatchReport;